import { useQuery } from '@apollo/client';
import type { FC } from 'react';
import { useState } from 'react';
import { useIntl } from 'react-intl';

import { Button } from '@xing-com/button';

import { RecommendedCompaniesSectionDocument } from '../../graphql/queries/recommended-companies-section.gql-types';
import { useScreen } from '../../hooks/use-screen/use-screen';
import { createSimpleCompanies } from '../../models/simple-company';
import { SectionContainer } from '../section-container/section-container';
import { SimpleCompanyCardGrid } from '../simple-company-card-grid/simple-company-card-grid';

import * as Styled from './section-recommended-companies.styles';

type SectionRecommendedCompaniesProps = {
  trackingPage: string;
};

export const SectionRecommendedCompanies: FC<
  SectionRecommendedCompaniesProps
> = ({ trackingPage }) => {
  const { isSmallScreen } = useScreen();
  const { $t } = useIntl();

  const [isFetchingMore, setIsFetchingMore] = useState(false);

  const limit = !isSmallScreen ? 18 : 6;

  const { data, loading, fetchMore } = useQuery(
    RecommendedCompaniesSectionDocument,
    {
      variables: {
        first: limit,
      },
    }
  );

  const hasNextPage =
    data?.viewer?.companiesRecommendations?.pageInfo.hasNextPage;
  const companiesEdges = data?.viewer?.companiesRecommendations?.edges ?? [];
  const companiesNodes = companiesEdges
    .map((edge) => edge?.node)
    .filter((node) => !!node);

  // @ts-expect-error graphql types
  const companiesCards = createSimpleCompanies(companiesNodes);

  const handleShowMore = async () => {
    setIsFetchingMore(true);
    await fetchMore({
      variables: {
        after: data?.viewer?.companiesRecommendations?.pageInfo.endCursor,
      },
      // @ts-expect-error graphql types
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;

        return {
          ...fetchMoreResult,
          viewer: {
            ...fetchMoreResult?.viewer,
            companiesRecommendations: {
              ...fetchMoreResult?.viewer?.companiesRecommendations,
              edges: [
                ...(prev?.viewer?.companiesRecommendations?.edges ?? []),
                ...(fetchMoreResult?.viewer?.companiesRecommendations?.edges ??
                  []),
              ],
            },
          },
        };
      },
    });
    setIsFetchingMore(false);
  };

  if (!loading && companiesCards.length === 0) {
    return null;
  }

  return (
    <SectionContainer
      title="CP_OVERVIEW_RECOMMENDATIONS_TITLE"
      subtitle="CP_OVERVIEW_RECOMMENDATIONS_DESCRIPTION"
    >
      {loading ? (
        <SimpleCompanyCardGrid.Skeleton amountOfCards={limit} />
      ) : (
        <>
          <SimpleCompanyCardGrid
            items={companiesCards}
            trackingPage={trackingPage}
          />
          {hasNextPage && (
            <Styled.ShowMoreWrapper>
              <Button
                data-testid="SHOW-MORE-RECOMMENDATIONS"
                variant="secondary"
                loading={isFetchingMore}
                onClick={handleShowMore}
              >
                {$t({ id: 'CP_OVERVIEW_RECOMMENDATIONS_LOAD_MORE_CTA' })}
              </Button>
            </Styled.ShowMoreWrapper>
          )}
        </>
      )}
    </SectionContainer>
  );
};
